import React, {FormEvent, useState} from 'react';
import {Duration} from "luxon";
import {toTitleCase} from "services/string";

export enum DurationUnit {
    // noinspection JSUnusedGlobalSymbols
    YEAR = 'years',
    MONTH = 'months',
    WEEK = 'weeks',
    DAY = 'days',
    HOUR = 'hours',
    MINUTE = 'minutes',
    SECONDS = 'seconds'
}

type Props = {
    value?: Duration | string | null
    units?: Array<DurationUnit>
    onChange: (duration: Duration) => any
    required?: boolean
}

function parseDuration(value: Duration | string | null): Duration {
    if (!value) return Duration.fromISO('PT0S')

    if (typeof value === 'string') return Duration.fromISO(value)

    return Duration.fromDurationLike(value)
}

function unitName(unit: DurationUnit, value: number): string {
    return toTitleCase(
        value === 1? unit.slice(0, -1) : unit
    )
}

function ExtendedDurationPicker({value, units, onChange, required}: Props) {
    const [duration, setDuration] = useState<Duration>(parseDuration(value || null));

    const showUnits = Object.values(units || DurationUnit)

    const handleChange = (value: string, unit: DurationUnit) => {
        const unitValues = duration.toObject()
        delete unitValues['milliseconds']

        if (value === '') {
            delete unitValues[unit]
        } else {
            unitValues[unit] = Number(value)
        }

        const updatedDuration = Duration.fromDurationLike(unitValues)

        setDuration(updatedDuration)
        onChange && onChange(updatedDuration)
    }

    return (
        <div className={`lg:flex lg:space-x-1`}>
            {showUnits.map(unit => (
                <div key={unit} className="relative mt-1 rounded-md shadow-sm w-1/3 lg:w-24">
                    <input
                        type="number"
                        step={1}
                        min={0}
                        name={`duration-${unit}`}
                        id={`duration-${unit}`}
                        className="block w-full rounded-md border-gray-300 pr-12 focus:border-brand focus:ring-brand sm:text-sm"
                        placeholder="0"
                        aria-describedby="duration-unit"
                        value={duration.toObject()[unit] ?? ''}
                        onChange={(event: FormEvent<HTMLInputElement>) => handleChange(event.currentTarget.value, unit)}
                        required={required && duration.toString() === 'PT0S'}
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-gray-500 sm:text-sm" id="duration-unit">
                            {unitName(unit, duration[unit])}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    )
}

ExtendedDurationPicker.defaultProps = {
    required: false
}

export default ExtendedDurationPicker;